import { v4 } from 'uuid';
import TYPES from '@/types';

// Application
import { GetIdentityVerificationStatusQuery }
  from '@/modules/onboarding/identity-verification/application/queries';
import GetInternetStatusQuery
  from '@/modules/internet-status/application/queries/get-internet-status-query';
import { GetOnboardingStepQuery } from '@/modules/onboarding/status/application/queries';
import OnboardingSearchStepsQuery
  from '@/modules/onboarding/status/application/queries/onboarding-search-steps-query';
import { CreateOnboardingStepCommand } from '@/modules/onboarding/status/application/commands';

// Domain
import { StepEntity } from '@/modules/onboarding/status/domain/entities';
import Inject from '@/modules/shared/domain/di/inject';
import { MessageNotifier } from '@/modules/shared/domain/notifiers/message_notifier';

export default class VerifyIdentityPageViewModel {
  @Inject(TYPES.GET_IDENTITY_VERIFICATION_STATUS_QUERY)
  private readonly get_identity_verification_status_query!: GetIdentityVerificationStatusQuery;

  @Inject(TYPES.GET_INTERNET_STATUS_QUERY)
  private readonly get_internet_status_query!: GetInternetStatusQuery;

  @Inject(TYPES.GET_ONBOARDING_STEP_QUERY)
  private readonly get_on_boarding_step_query!: GetOnboardingStepQuery;

  @Inject(TYPES.ONBOARDING_SEARCH_STEPS_QUERY)
  private readonly get_all_boarding_steps_query!: OnboardingSearchStepsQuery;

  @Inject(TYPES.CREATE_ONBOARDING_STEP_COMMAND)
  readonly create_onboarding_step_command!: CreateOnboardingStepCommand;

  @Inject(TYPES.NOTIFIER)
  readonly message_notifier!: MessageNotifier;

  private current_step: StepEntity = {
    current_step: '',
    id: '',
    payload: {},
  };

  readonly step_ids = {
    upload_documents: v4(),
  };

  is_loading = true;

  verification_status = 'pending';

  client_was_added_on_siap_web = false;

  get last_event_data() {
    return this.get_identity_verification_status_query.execute();
  }

  get identify_verification_succeed() {
    return (
      (
        this.last_event_data.success
        && this.last_event_data.percent === 100
      )
      || this.current_step.payload.status === 'verified'
    );
  }

  get hide_btn_continue() {
    return this.identify_verification_succeed && this.client_was_added_on_siap_web;
  }

  get can_continue() {
    return (
      this.identify_verification_succeed
      && !this.is_loading
      && this.get_internet_status_query.execute()
    );
  }

  updateVerificationStatus = () => {
    if (this.last_event_data.percent === 100) {
      this.is_loading = false;
      this.verification_status = this.last_event_data.status;
      return;
    }

    if (this.current_step.payload.status) {
      this.verification_status = this.current_step.payload.status;
      return;
    }

    if (!this.last_event_data.success) {
      this.is_loading = false;
      this.verification_status = 'has_error';
    }
  }

  setCurrentStep = async () => {
    this.current_step = await this.get_on_boarding_step_query.execute('identity_verification');
    if (this.current_step.payload.status === 'verified') this.is_loading = false;
  }

  searchNewClientWasAddedStep = async () => {
    try {
      const steps = await this.get_all_boarding_steps_query.execute();
      const new_client_was_added = steps.find((step) => step.current_step === 'new_client_was_added');
      if (new_client_was_added) this.client_was_added_on_siap_web = true;
    } catch {
      this.message_notifier.showErrorNotification('Ocurrió un error al obtener su progreso actual, favor de recargar la página');
    }
  }

  createNextOnboardingStep = async () => {
    try {
      await this.create_onboarding_step_command.execute({
        id: this.step_ids.upload_documents,
        current_step: 'upload_documents',
        payload: {},
      });
    } catch {
      this.message_notifier.showErrorNotification('Ocurrió un error al guardar su progreso');
    }
  }
}
